import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api.js';
import { Input } from '@/components/ui/input.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { Field, Txt, Num, Sel, Toggle, Group, LadderTable, money } from '@/components/bno/bno-kit.jsx';
import { AlertTriangle, ChevronDown, ChevronRight, Download, FlaskConical, Play, RotateCw } from 'lucide-react';
import { ymdFromNow } from '@/lib/date-display.js';

// BNO RATING EXPLORER (CTR-RATING-EXPLORER-CC-HANDOFF).
//
// A broker-only replica of Global's insured portal over the UAT API: every
// field of NonOwnedQuoteRequest as a control, grouped the way the portal
// groups them, with the model path printed under each control so David can
// map what he sees to the reference doc. Submits go to /api/ctr/dev/bno/*,
// which is JWT-gated and REFUSES to run against anything but UAT.
//
// Replicate the model, not the pixels (decision 3). Never bind, never
// release except through the double-gated release door (decision 4). Every
// submit is persisted server-side to bno_uat_quotes (decision 5).
//
// The right column renders the RatingResults ladders from ONE submit. Rows
// are clickable; the client-side total is the sum of the selected rungs, and
// "Re-quote at these limits" fires Global's edit to test whether the ladder
// is genuinely additive (the mismatch, if any, is displayed, not hidden).

const CERTIFICATES = ['NONE', 'STUDENT', 'RECREATIONAL', 'SPORT', 'PRIVATE', 'COMMERCIAL', 'AIRLINETRANSPORTPILOT'];
const RATINGS = ['INSTRUMENT', 'ASEL', 'AMEL', 'ASES', 'GLIDER', 'CFI', 'MCFI'];
const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];
// Coverage limits are NOT constants: config.rungs carries the captured
// RatingResults ladders and the datalists render exactly what Global sells.
// AssociationType is a required enum next to the free-text name (drift log #4).
const ASSOCIATIONS = [{ v: 1, label: 'AOPA' }, { v: 2, label: 'EAA' }];

const clone = (o) => JSON.parse(JSON.stringify(o));
const g = (o, k) => (o == null ? undefined : (o[k] !== undefined ? o[k] : o[k.charAt(0).toLowerCase() + k.slice(1)]));

function plusDays(days) {
  return ymdFromNow(days);
}

// ─── presets (handoff §4: the presets become school-config defaults) ────────

function buildPresets(baseline) {
  const from = (mutate) => { const p = clone(baseline); mutate(p); return p; };
  return {
    'Student renter': from((p) => {
      p.Pilot.Age = 22; p.Pilot.Certificate = 'STUDENT'; p.Pilot.Ratings = [];
      p.Pilot.TotalHours = 38; p.Pilot.Last12MonthHours = 38;
      p.Insured.FirstName = p.Pilot.FirstName = 'Maya'; p.Insured.LastName = p.Pilot.LastName = 'Okafor';
    }),
    'Private renter': from(() => {}),
    'CFI': from((p) => {
      p.IsCfi = true; p.Uses.CFI = true; p.Pilot.Certificate = 'COMMERCIAL';
      p.Pilot.Ratings = ['ASEL', 'INSTRUMENT', 'CFI']; p.Pilot.TotalHours = 800; p.Pilot.Last12MonthHours = 200;
    }),
    'Business-travel employer': from((p) => {
      p.IsEmployerCovered = true; p.EmployerCoverage = { EmployerName: 'Acme Consulting LLC' };
    }),
    'High-risk decline': from((p) => { p.Pilot.Age = 92; }),
  };
}

// ─── the page ───────────────────────────────────────────────────────────────
// Form atoms + LadderTable moved to components/bno/bno-kit.jsx (shared with
// the BNO Quote Desk sheet: same ladder component, one home, no fork).

export default function DevBnoExplorer() {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);          // { parsed, raw, row_id } or { error, response }
  const [sel, setSel] = useState({});                  // { liability, damage, ded, medical, employerOn }
  const [requote, setRequote] = useState(null);        // edit response for the additivity check
  const [savedBaseline, setSavedBaseline] = useState(null);
  const [sweep, setSweep] = useState({ running: false, rows: [], runId: null });
  const [sweepSort, setSweepSort] = useState('order'); // 'order' | 'delta'
  const [showRaw, setShowRaw] = useState(false);
  const sweepAbort = useRef(null);

  useEffect(() => {
    api.get('/api/ctr/dev/bno/config').then((r) => {
      setConfig(r);
      const p = clone(r.baseline);
      p.CoverageDate = plusDays(7);
      setForm(p);
    }).catch((e) => toast.error(`Explorer config failed: ${e.message}`));
  }, []);

  const presets = useMemo(() => (config ? buildPresets({ ...clone(config.baseline), CoverageDate: plusDays(7) }) : {}), [config]);

  // Path-based setter so controls edit the payload structure directly. The
  // form state IS the NonOwnedQuoteRequest that gets submitted.
  const set = useCallback((path, value) => {
    setForm((prev) => {
      const next = clone(prev);
      const keys = path.split('.');
      let o = next;
      for (let i = 0; i < keys.length - 1; i++) { o[keys[i]] = o[keys[i]] || {}; o = o[keys[i]]; }
      o[keys[keys.length - 1]] = value;
      return next;
    });
  }, []);

  // ── quote ──
  async function quote() {
    setBusy(true); setRequote(null);
    try {
      const r = await api.post('/api/ctr/dev/bno/quote', { payload: form, label: 'explorer' }, { timeoutMs: 120000 });
      setResult(r);
      if (r.success) {
        const L = r.parsed.ladders;
        // Preselect the rung matching what was applied for, so the client-side
        // total starts at Global's own total instead of at nothing.
        setSel({
          liability: idxOfLiability(L.liability, form.LiabilityCoverage),
          damage: form.IsDamageNonOwnedLiabilityCovered ? idxOfLimit(L.damage, form.DamageNonOwnedLiabilityCoverage?.HullLimit) : null,
          ded: form.IsDeductibleReimbursementCovered ? idxOfLimit(L.deductibleReimb, form.DeductibleReimbursementCoverage?.CoverageLimit) : null,
          medical: form.IsLiabilityMedicalCovered ? idxOfLimit(L.medical, form.LiabilityMedicalCoverage?.CoverageLimit) : null,
          employerOn: !!form.IsEmployerCovered,
        });
        toast.success(`Quote ${r.parsed.quoteNumber}: ${r.parsed.status}, total ${money(r.parsed.total)}`);
      } else {
        toast.error(`Global returned HTTP ${r.status ?? '?'}. Details on the page.`);
      }
    } catch (e) {
      toast.error(`Quote failed: ${e.message}`);
    } finally { setBusy(false); }
  }

  function idxOfLimit(rows, limit) {
    if (!rows || limit == null) return null;
    const i = rows.findIndex((r) => Number(r.limit) === Number(limit));
    return i >= 0 ? i : null;
  }
  function idxOfLiability(rows, cov) {
    if (!rows || !cov) return null;
    const i = rows.findIndex((r) => Number(r.limit) === Number(cov.CoverageLimit)
      && !!r.excludingPassengers === !!cov.ExcludingPassengers
      && (cov.ExcludingPassengers || Number(r.paxSubLimit) === Number(cov.PassengerSubLimit)));
    return i >= 0 ? i : idxOfLimit(rows, cov.CoverageLimit);
  }

  const ladders = result?.success ? result.parsed.ladders : null;

  // Client-side total: sum of the selected rungs. Whether Global agrees is
  // exactly what "Re-quote at these limits" measures.
  const selectedTotal = useMemo(() => {
    if (!ladders) return null;
    let t = 0;
    const add = (rows, idx) => { if (idx != null && rows[idx]) t += Number(rows[idx].premium) || 0; };
    add(ladders.liability, sel.liability);
    add(ladders.damage, sel.damage);
    add(ladders.deductibleReimb, sel.ded);
    add(ladders.medical, sel.medical);
    if (sel.employerOn && ladders.employer) t += Number(ladders.employer.premium) || 0;
    return t;
  }, [ladders, sel]);

  // ── re-quote at the selected limits (the additivity test) ──
  async function requoteAtSelection() {
    if (!ladders || !result?.raw) return;
    const echo = g(result.raw, 'NonOwnedQuoteRequest') || {};
    const p = clone(form);
    p.QuoteNumber = g(result.raw, 'QuoteNumber');
    p.QuoteGuid = g(echo, 'QuoteGuid');
    p.SessionGuid = g(echo, 'SessionGuid');
    const liab = sel.liability != null ? ladders.liability[sel.liability] : null;
    if (liab) p.LiabilityCoverage = { CoverageLimit: liab.limit, ExcludingPassengers: !!liab.excludingPassengers, PassengerSubLimit: liab.excludingPassengers ? null : liab.paxSubLimit };
    const dmg = sel.damage != null ? ladders.damage[sel.damage] : null;
    p.IsDamageNonOwnedLiabilityCovered = !!dmg;
    if (dmg) p.DamageNonOwnedLiabilityCoverage = { HullLimit: dmg.limit }; else delete p.DamageNonOwnedLiabilityCoverage;
    const ded = sel.ded != null ? ladders.deductibleReimb[sel.ded] : null;
    p.IsDeductibleReimbursementCovered = !!ded;
    if (ded) p.DeductibleReimbursementCoverage = { CoverageLimit: ded.limit }; else delete p.DeductibleReimbursementCoverage;
    const med = sel.medical != null ? ladders.medical[sel.medical] : null;
    p.IsLiabilityMedicalCovered = !!med;
    // The medical OBJECT stays even when off: its absence 500s the rater
    // (BNO-QUOTE-FIELD-MAP.md drift log #2).
    p.LiabilityMedicalCoverage = med ? { CoverageLimit: med.limit, Included: false } : (p.LiabilityMedicalCoverage || { CoverageLimit: 5000, Included: false });
    p.IsEmployerCovered = !!sel.employerOn;
    if (!sel.employerOn) delete p.EmployerCoverage;

    setBusy(true);
    try {
      const r = await api.post('/api/ctr/dev/bno/edit', { payload: p, label: 'requote at selection' }, { timeoutMs: 120000 });
      setRequote(r);
      if (r.success) {
        const gt = r.parsed.total;
        const match = gt != null && selectedTotal != null && Math.abs(gt - selectedTotal) < 0.5;
        toast[match ? 'success' : 'error'](match
          ? `Global agrees: ${money(gt)}`
          : `Global says ${money(gt)}, client-side sum was ${money(selectedTotal)}`);
      } else {
        toast.error(`Edit returned HTTP ${r.status ?? '?'}. Details on the page.`);
      }
    } catch (e) {
      toast.error(`Re-quote failed: ${e.message}`);
    } finally { setBusy(false); }
  }

  // ── sweep (NDJSON stream) ──
  async function runSweep() {
    if (sweep.running) return;
    setSweep({ running: true, rows: [], runId: null });
    const token = localStorage.getItem('tracon_token');
    const ctrl = new AbortController();
    sweepAbort.current = ctrl;
    try {
      const res = await fetch('/api/ctr/dev/bno/sweep', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ baseline: savedBaseline || form }),
        signal: ctrl.signal,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop();
        for (const line of lines.filter(Boolean)) {
          const msg = JSON.parse(line);
          if (msg.kind === 'result') setSweep((s) => ({ ...s, rows: [...s.rows, msg] }));
          else if (msg.kind === 'start') setSweep((s) => ({ ...s, runId: msg.sweep_run_id }));
        }
      }
      toast.success('Sweep complete');
    } catch (e) {
      if (e.name !== 'AbortError') toast.error(`Sweep failed: ${e.message}`);
    } finally {
      setSweep((s) => ({ ...s, running: false }));
      sweepAbort.current = null;
    }
  }

  const sweepRows = useMemo(() => {
    if (sweepSort === 'order') return sweep.rows;
    return [...sweep.rows].sort((a, b) => Math.abs(b.delta ?? 0) - Math.abs(a.delta ?? 0));
  }, [sweep.rows, sweepSort]);

  if (!config || !form) {
    return <div className="p-6 text-sm text-text-dim">Loading the explorer configuration...</div>;
  }

  const memberships = form.Insured.Memberships || [];
  const parsed = result?.success ? result.parsed : null;

  return (
    <div className="p-4 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FlaskConical className="h-5 w-5 text-info" />
          <h1 className="text-lg font-bold text-text">BNO Rating Explorer</h1>
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-warning-soft text-warning font-medium">UAT only, never binds</span>
        </div>
        <div className="flex items-center gap-2">
          <select className="h-8 rounded-md border border-border bg-bg px-2 text-sm"
            defaultValue="" onChange={(e) => { if (presets[e.target.value]) { setForm(clone(presets[e.target.value])); setResult(null); setRequote(null); } e.target.value = ''; }}>
            <option value="" disabled>Presets...</option>
            {Object.keys(presets).map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={() => { setSavedBaseline(clone(form)); toast.success('Saved as sweep baseline'); }}>
            Save as baseline
          </Button>
          <Button size="sm" variant="outline" disabled={sweep.running} onClick={runSweep}>
            {sweep.running ? <RotateCw className="h-3.5 w-3.5 animate-spin mr-1" /> : <Play className="h-3.5 w-3.5 mr-1" />}
            Run lever sweep
          </Button>
          <Button size="sm" disabled={busy} onClick={quote}>
            {busy ? <RotateCw className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
            Quote
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* ── LEFT: the form, every field of the model ── */}
        <div className="space-y-3">
          <Group title="Applicant">
            <div className="grid grid-cols-2 gap-2">
              <Field label="First name" hint="Insured.FirstName + Pilot.FirstName">
                <Txt value={form.Insured.FirstName} onChange={(v) => { set('Insured.FirstName', v); set('Pilot.FirstName', v); }} />
              </Field>
              <Field label="Last name" hint="Insured.LastName + Pilot.LastName">
                <Txt value={form.Insured.LastName} onChange={(v) => { set('Insured.LastName', v); set('Pilot.LastName', v); }} />
              </Field>
              <Field label="Address line 1" hint="Insured.AddressLine1 (required at quote, drift log #3)" className="col-span-2">
                <Txt value={form.Insured.AddressLine1} onChange={(v) => set('Insured.AddressLine1', v)} />
              </Field>
              <Field label="City" hint="Insured.City">
                <Txt value={form.Insured.City} onChange={(v) => set('Insured.City', v)} />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="State" hint="Insured.StateCode">
                  <Sel value={form.Insured.StateCode} onChange={(v) => set('Insured.StateCode', v)} options={US_STATES} />
                  {Array.isArray(config.licensed_states) && form.Insured.StateCode && !config.licensed_states.includes(form.Insured.StateCode) ? (
                    <span className="inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-warning-soft text-warning">
                      Not in PRODUCER_LICENSED_STATES
                    </span>
                  ) : null}
                </Field>
                <Field label="Zip" hint="Insured.ZipCode">
                  <Txt value={form.Insured.ZipCode} onChange={(v) => set('Insured.ZipCode', v)} />
                </Field>
              </div>
              <Field label="Email" hint="Insured.EmailAddress">
                <Txt value={form.Insured.EmailAddress} onChange={(v) => set('Insured.EmailAddress', v)} />
              </Field>
              <Field label="Phone" hint="Insured.PhoneNumber">
                <Txt value={form.Insured.PhoneNumber} onChange={(v) => set('Insured.PhoneNumber', v)} />
              </Field>
            </div>
            <div>
              <div className="text-xs font-medium text-text-dim mb-1">Memberships <span className="font-mono text-[10px] text-text-dim">Insured.Memberships[] (AssociationType required, drift log #4)</span></div>
              {memberships.map((m, i) => (
                <div key={i} className="flex items-center gap-2 mb-1">
                  <Sel value={String(m.AssociationType ?? '')} options={ASSOCIATIONS.map((a) => ({ value: String(a.v), label: a.label }))}
                    onChange={(v) => { const next = clone(memberships); next[i].AssociationType = Number(v); next[i].MembershipAssociation = ASSOCIATIONS.find((a) => a.v === Number(v))?.label || ''; set('Insured.Memberships', next); }} />
                  <Txt placeholder="Member number" value={m.MembershipCode}
                    onChange={(v) => { const next = clone(memberships); next[i].MembershipCode = v; set('Insured.Memberships', next); }} />
                  <Button size="sm" variant="ghost" onClick={() => set('Insured.Memberships', memberships.filter((_, j) => j !== i))}>Remove</Button>
                </div>
              ))}
              <Button size="sm" variant="outline"
                onClick={() => set('Insured.Memberships', [...memberships, { AssociationType: 1, MembershipAssociation: 'AOPA', MembershipCode: '' }])}>
                Add membership
              </Button>
            </div>
          </Group>

          <Group title="Pilot">
            <div className="grid grid-cols-3 gap-2">
              <Field label="Age" hint="Pilot.Age (at inception)">
                <Num value={form.Pilot.Age} onChange={(v) => set('Pilot.Age', v)} />
              </Field>
              <Field label="Certificate" hint="Pilot.Certificate" className="col-span-2">
                <Sel value={form.Pilot.Certificate} onChange={(v) => set('Pilot.Certificate', v)} options={CERTIFICATES} />
              </Field>
            </div>
            <Field label="Ratings" hint="Pilot.Ratings[] (empty is valid for a student)">
              <div className="flex flex-wrap gap-1 mt-1">
                {RATINGS.map((r) => {
                  const on = (form.Pilot.Ratings || []).includes(r);
                  return (
                    <button key={r} type="button"
                      className={`px-2 py-0.5 rounded-full text-xs border ${on ? 'bg-accent text-white border-accent' : 'bg-surface text-text-dim border-border'}`}
                      onClick={() => set('Pilot.Ratings', on ? form.Pilot.Ratings.filter((x) => x !== r) : [...(form.Pilot.Ratings || []), r])}>
                      {r}
                    </button>
                  );
                })}
              </div>
            </Field>
            <div className="grid grid-cols-3 gap-2">
              <Field label="Total hours" hint="Pilot.TotalHours">
                <Num value={form.Pilot.TotalHours} onChange={(v) => set('Pilot.TotalHours', v)} />
              </Field>
              <Field label="Last 12 months" hint="Pilot.Last12MonthHours">
                <Num value={form.Pilot.Last12MonthHours} onChange={(v) => set('Pilot.Last12MonthHours', v)} />
              </Field>
              <Field label="Retractable" hint="Pilot.RetractableGearHours">
                <Num value={form.Pilot.RetractableGearHours} onChange={(v) => set('Pilot.RetractableGearHours', v)} />
              </Field>
              <Field label="Tailwheel" hint="Pilot.ConventionalGearHours">
                <Num value={form.Pilot.ConventionalGearHours} onChange={(v) => set('Pilot.ConventionalGearHours', v)} />
              </Field>
              <Field label="Multi-engine" hint="Pilot.MultiEngineHours">
                <Num value={form.Pilot.MultiEngineHours} onChange={(v) => set('Pilot.MultiEngineHours', v)} />
              </Field>
              <Field label="Seaplane" hint="Pilot.SeaPlaneHours">
                <Num value={form.Pilot.SeaPlaneHours} onChange={(v) => set('Pilot.SeaPlaneHours', v)} />
              </Field>
            </div>
            <Toggle label="Violations, suspensions, DUI or felony in the last 5 years" hint="Pilot.ClaimsQuestions.ViolationsOrConvictions"
              checked={form.Pilot.ClaimsQuestions?.ViolationsOrConvictions}
              onChange={(v) => set('Pilot.ClaimsQuestions.ViolationsOrConvictions', v)} />
            <Toggle label="Accidents, incidents or losses in the last 5 years" hint="Pilot.ClaimsQuestions.IncidentsOrAccidents"
              checked={form.Pilot.ClaimsQuestions?.IncidentsOrAccidents}
              onChange={(v) => set('Pilot.ClaimsQuestions.IncidentsOrAccidents', v)} />
            {(form.Pilot.ClaimsQuestions?.ViolationsOrConvictions || form.Pilot.ClaimsQuestions?.IncidentsOrAccidents) ? (
              <Field label="Details" hint="Pilot.ClaimsDetails (required when either is yes)">
                <textarea className="mt-0.5 w-full rounded-md border border-border bg-bg px-2 py-1 text-sm" rows={2}
                  value={form.Pilot.ClaimsDetails || ''} onChange={(e) => set('Pilot.ClaimsDetails', e.target.value)} />
              </Field>
            ) : null}
          </Group>

          <Group title="Aircraft and use">
            <div className="flex gap-4">
              <Toggle label="SEL" hint="AircraftTypes.SEL" checked={form.AircraftTypes.SEL} onChange={(v) => set('AircraftTypes.SEL', v)} />
              <Toggle label="SES" hint="AircraftTypes.SES" checked={form.AircraftTypes.SES} onChange={(v) => set('AircraftTypes.SES', v)} />
              <Toggle label="MEL" hint="AircraftTypes.MEL" checked={form.AircraftTypes.MEL} onChange={(v) => set('AircraftTypes.MEL', v)} />
            </div>
            <div className="flex gap-4">
              <Toggle label="Pleasure / business" hint="Uses.PleasureBusiness" checked={form.Uses.PleasureBusiness} onChange={(v) => set('Uses.PleasureBusiness', v)} />
              <Toggle label="CFI use" hint="Uses.CFI" checked={form.Uses.CFI} onChange={(v) => set('Uses.CFI', v)} />
              <Toggle label="Is CFI (product path)" hint="IsCfi" checked={form.IsCfi} onChange={(v) => set('IsCfi', v)} />
            </div>
          </Group>

          <Group title="Coverages">
            <div className="grid grid-cols-3 gap-2">
              <Field label="Liability limit" hint="LiabilityCoverage.CoverageLimit (rungs from the captured ladder)">
                <Input className="h-8 mt-0.5" list="liab-limits" type="number" value={form.LiabilityCoverage?.CoverageLimit ?? ''}
                  onChange={(e) => set('LiabilityCoverage.CoverageLimit', e.target.value === '' ? null : Number(e.target.value))} />
                <datalist id="liab-limits">{[...new Set((config.rungs?.liability || []).map((r) => r.limit))].map((l) => <option key={l} value={l} />)}</datalist>
              </Field>
              <Field label="Pax sublimit" hint="LiabilityCoverage.PassengerSubLimit">
                <Num value={form.LiabilityCoverage?.PassengerSubLimit} onChange={(v) => set('LiabilityCoverage.PassengerSubLimit', v)}
                  disabled={form.LiabilityCoverage?.ExcludingPassengers} />
              </Field>
              <div className="pt-4">
                <Toggle label="Excluding passengers" hint="ExcludingPassengers"
                  checked={form.LiabilityCoverage?.ExcludingPassengers}
                  onChange={(v) => { set('LiabilityCoverage.ExcludingPassengers', v); if (v) set('LiabilityCoverage.PassengerSubLimit', null); }} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 items-end">
              <Toggle label="Non-owned aircraft damage" hint="IsDamageNonOwnedLiabilityCovered"
                checked={form.IsDamageNonOwnedLiabilityCovered}
                onChange={(v) => { set('IsDamageNonOwnedLiabilityCovered', v); if (v && !form.DamageNonOwnedLiabilityCoverage) set('DamageNonOwnedLiabilityCoverage', { HullLimit: 40000 }); }} />
              {form.IsDamageNonOwnedLiabilityCovered ? (
                <Field label="Hull limit" hint="DamageNonOwnedLiabilityCoverage.HullLimit (rungs from the captured ladder)">
                  <Num list="dmg-limits" value={form.DamageNonOwnedLiabilityCoverage?.HullLimit} onChange={(v) => set('DamageNonOwnedLiabilityCoverage.HullLimit', v)} />
                  <datalist id="dmg-limits">{(config.rungs?.damage || []).map((r) => <option key={r.limit} value={r.limit} />)}</datalist>
                </Field>
              ) : null}
            </div>
            <div className="grid grid-cols-2 gap-2 items-end">
              <Toggle label="Deductible reimbursement" hint="IsDeductibleReimbursementCovered (NOT the PDF spelling)"
                checked={form.IsDeductibleReimbursementCovered}
                onChange={(v) => { set('IsDeductibleReimbursementCovered', v); if (v && !form.DeductibleReimbursementCoverage) set('DeductibleReimbursementCoverage', { CoverageLimit: 5000 }); }} />
              {form.IsDeductibleReimbursementCovered ? (
                <Field label="Limit" hint="DeductibleReimbursementCoverage.CoverageLimit (rungs from the captured ladder)">
                  <Num list="ded-limits" value={form.DeductibleReimbursementCoverage?.CoverageLimit} onChange={(v) => set('DeductibleReimbursementCoverage.CoverageLimit', v)} />
                  <datalist id="ded-limits">{(config.rungs?.deductibleReimb || []).map((r) => <option key={r.limit} value={r.limit} />)}</datalist>
                </Field>
              ) : null}
            </div>
            <div className="grid grid-cols-2 gap-2 items-end">
              <Toggle label="Medical payments" hint="IsLiabilityMedicalCovered (object always sent, drift log #2)"
                checked={form.IsLiabilityMedicalCovered}
                onChange={(v) => set('IsLiabilityMedicalCovered', v)} />
              <Field label="Limit" hint="LiabilityMedicalCoverage.CoverageLimit (object always rates; the first rung is the free one)">
                <Num list="med-limits" value={form.LiabilityMedicalCoverage?.CoverageLimit} onChange={(v) => set('LiabilityMedicalCoverage.CoverageLimit', v)} />
                <datalist id="med-limits">{(config.rungs?.medical || []).map((r) => <option key={r.limit} value={r.limit} />)}</datalist>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-2 items-end">
              <Toggle label="Employer as additional insured" hint="IsEmployerCovered (the pilot's employer, not the school)"
                checked={form.IsEmployerCovered}
                onChange={(v) => { set('IsEmployerCovered', v); if (v && !form.EmployerCoverage) set('EmployerCoverage', { EmployerName: '' }); if (!v) set('EmployerCoverage', undefined); }} />
              {form.IsEmployerCovered ? (
                <Field label="Employer name" hint="EmployerCoverage.EmployerName">
                  <Txt value={form.EmployerCoverage?.EmployerName} onChange={(v) => set('EmployerCoverage.EmployerName', v)} />
                </Field>
              ) : null}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Field label="Min liability" hint="MinimumLiabilityLimit">
                <Num value={form.MinimumLiabilityLimit} onChange={(v) => set('MinimumLiabilityLimit', v)} />
              </Field>
              <Field label="Min pax sublimit" hint="MinimumPaxSubLimit">
                <Num value={form.MinimumPaxSubLimit} onChange={(v) => set('MinimumPaxSubLimit', v)} />
              </Field>
              <Field label="Min damage limit" hint="MinimumDamageNonOwnedLiabilityLimit">
                <Num value={form.MinimumDamageNonOwnedLiabilityLimit} onChange={(v) => set('MinimumDamageNonOwnedLiabilityLimit', v)} />
              </Field>
            </div>
          </Group>

          <Group title="Extras" defaultOpen={false}>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Coverage date" hint="CoverageDate (today to +90d)">
                <Input className="h-8 mt-0.5" type="date" value={form.CoverageDate || ''} onChange={(e) => set('CoverageDate', e.target.value)} />
              </Field>
              <Field label="Flight school" hint="FlightSchool (attribution tag)">
                <Txt value={form.FlightSchool} onChange={(v) => set('FlightSchool', v)} />
              </Field>
              <Field label="Datasource" hint="Datasource (from config, read-only)">
                <Input className="h-8 mt-0.5" value={form.Datasource || ''} readOnly disabled />
              </Field>
              <Field label="Referral email" hint="ReferralEmailAddress">
                <Txt value={form.ReferralEmailAddress} onChange={(v) => set('ReferralEmailAddress', v)} />
              </Field>
              <Field label="Retail broker agency" hint="RetailBrokerAgency">
                <Txt value={form.RetailBrokerAgency} onChange={(v) => set('RetailBrokerAgency', v)} />
              </Field>
              <Field label="Comments" hint="Comments" className="col-span-2">
                <textarea className="mt-0.5 w-full rounded-md border border-border bg-bg px-2 py-1 text-sm" rows={2}
                  value={form.Comments || ''} onChange={(e) => set('Comments', e.target.value)} />
              </Field>
            </div>
          </Group>
        </div>

        {/* ── RIGHT: pricing ── */}
        <div className="space-y-3">
          {result && !result.success ? (
            <div className="border border-danger bg-danger-soft rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-danger">
                <AlertTriangle className="h-4 w-4" /> Global returned HTTP {result.status ?? 'error'}
              </div>
              <pre className="mt-2 text-xs text-danger whitespace-pre-wrap break-all">{JSON.stringify(result.response || result.error, null, 2)}</pre>
              <div className="mt-1 text-[11px] text-danger">Field-name drift is recorded in BNO-QUOTE-FIELD-MAP.md; if this is a new one, add it there.</div>
            </div>
          ) : null}

          {parsed ? (
            <div className="border border-border rounded-lg bg-surface p-3 space-y-3">
              <div className="flex items-end justify-between">
                <div>
                  <div className="text-3xl font-bold text-text tabular-nums">{money(parsed.total)}</div>
                  <div className="text-xs text-text-dim">Quote {parsed.quoteNumber} on {String(parsed.coverageDate || '').slice(0, 10)}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-surface-strong text-text font-medium">{parsed.status}</span>
                  <div className="flex gap-1">
                    {parsed.referred ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-warning-soft text-warning">Referred</span> : null}
                    {parsed.declined ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-danger-soft text-danger">Declined</span> : null}
                    {parsed.surchargePercent ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-warning-soft text-warning">Surcharge {parsed.surchargePercent}%</span> : null}
                  </div>
                </div>
              </div>
              {(parsed.warnings || []).length ? (
                <div className="text-xs text-warning bg-warning-soft border border-warning rounded-md p-2">
                  {parsed.warnings.map((w, i) => <div key={i}>{w}</div>)}
                </div>
              ) : null}

              <LadderTable title="Liability" limitLabel="Limit" rows={parsed.ladders.liability}
                selectedIdx={sel.liability} onSelect={(i) => setSel((s) => ({ ...s, liability: i }))}
                renderLimit={(r) => `${money(r.limit)}${r.excludingPassengers ? ' excl pax' : (r.paxSubLimit ? ` / ${money(r.paxSubLimit)} pax` : '')}`} />
              <LadderTable title="Non-owned aircraft damage" limitLabel="Hull limit" rows={parsed.ladders.damage}
                selectedIdx={sel.damage} onSelect={(i) => setSel((s) => ({ ...s, damage: s.damage === i ? null : i }))}
                renderLimit={(r) => money(r.limit)} />
              <LadderTable title="Deductible reimbursement" limitLabel="Limit" rows={parsed.ladders.deductibleReimb}
                selectedIdx={sel.ded} onSelect={(i) => setSel((s) => ({ ...s, ded: s.ded === i ? null : i }))}
                renderLimit={(r) => money(r.limit)} />
              <LadderTable title="Medical payments" limitLabel="Limit" rows={parsed.ladders.medical}
                selectedIdx={sel.medical} onSelect={(i) => setSel((s) => ({ ...s, medical: s.medical === i ? null : i }))}
                renderLimit={(r) => money(r.limit)} />
              {parsed.ladders.employer ? (
                <Toggle label={`Employer additional insured: ${money(parsed.ladders.employer.premium)}`} hint="EmployerCoverageRatingResult"
                  checked={sel.employerOn} onChange={(v) => setSel((s) => ({ ...s, employerOn: v }))} />
              ) : null}

              <div className="flex items-center justify-between border-t border-border pt-2">
                <div>
                  <div className="text-xs text-text-dim">Selected rungs, client-side sum</div>
                  <div className="text-xl font-bold tabular-nums text-text">{money(selectedTotal)}</div>
                  {requote?.success ? (
                    <div className={`text-xs font-medium ${Math.abs((requote.parsed.total ?? 0) - (selectedTotal ?? 0)) < 0.5 ? 'text-success' : 'text-danger'}`}>
                      Global re-quoted: {money(requote.parsed.total)}
                      {Math.abs((requote.parsed.total ?? 0) - (selectedTotal ?? 0)) < 0.5 ? ' (matches: the ladder is additive)' : ' (does NOT match the client-side sum)'}
                    </div>
                  ) : null}
                  {requote && !requote.success ? (
                    <div className="text-xs text-danger">Edit failed: HTTP {requote.status ?? '?'} {typeof requote.response === 'object' ? JSON.stringify(requote.response).slice(0, 200) : ''}</div>
                  ) : null}
                </div>
                <Button size="sm" variant="outline" disabled={busy} onClick={requoteAtSelection}>Re-quote at these limits</Button>
              </div>
            </div>
          ) : (!result ? (
            <div className="border border-dashed border-border rounded-lg p-8 text-center text-sm text-text-dim">
              Pick a preset or fill the form, then Quote. The full ladder prices in one call.
            </div>
          ) : null)}

          {result ? (
            <div className="border border-border rounded-lg bg-surface">
              <button type="button" className="w-full flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-text" onClick={() => setShowRaw(!showRaw)}>
                {showRaw ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                Raw response
              </button>
              {showRaw ? <pre className="px-3 pb-3 text-[10px] text-text-dim overflow-x-auto max-h-96">{JSON.stringify(result.raw || result.response, null, 2)}</pre> : null}
            </div>
          ) : null}

          {sweep.rows.length || sweep.running ? (
            <div className="border border-border rounded-lg bg-surface p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-text">
                  Lever sweep {sweep.running ? `(running, ${sweep.rows.length} in)` : `(${sweep.rows.length} results)`}
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setSweepSort(sweepSort === 'delta' ? 'order' : 'delta')}>
                    Sort: {sweepSort === 'delta' ? 'by delta' : 'run order'}
                  </Button>
                  {sweep.runId ? (
                    <button type="button" className="inline-flex items-center gap-1 text-xs text-info hover:underline"
                      onClick={() => downloadCsv(sweep.runId)}>
                      <Download className="h-3.5 w-3.5" /> CSV
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-surface-strong text-text-dim">
                      {['Group', 'Lever', 'Status', 'Total', 'Delta', 'Liab', 'Dmg', 'Ded', 'Med', 'Emp', 'Surch%', 'Flags', 'Warnings'].map((h) => (
                        <th key={h} className="text-left px-2 py-1 font-medium whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sweepRows.map((r, i) => (
                      <tr key={i} className={`border-t border-border/60 ${r.error ? 'bg-danger-soft' : ''}`}>
                        <td className="px-2 py-1 text-text-dim">{r.group}</td>
                        <td className="px-2 py-1 whitespace-nowrap">{r.label}</td>
                        <td className="px-2 py-1 whitespace-nowrap">{r.error ? `HTTP ${r.http_status ?? 'ERR'}` : r.status}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{money(r.total)}</td>
                        <td className={`px-2 py-1 text-right tabular-nums font-medium ${r.delta > 0 ? 'text-danger' : r.delta < 0 ? 'text-success' : 'text-text-dim'}`}>
                          {r.delta == null ? '' : `${r.delta > 0 ? '+' : ''}${r.delta.toFixed(2)}`}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums">{money(r.liab)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{money(r.dmg)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{money(r.ded)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{money(r.med)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{money(r.emp)}</td>
                        <td className="px-2 py-1 text-right">{r.surcharge ?? ''}</td>
                        <td className="px-2 py-1">{r.referred ? 'REF ' : ''}{r.declined ? 'DECL' : ''}</td>
                        <td className="px-2 py-1 text-warning max-w-[240px]">
                          {r.error ? <span className="text-danger break-all">{typeof r.response === 'object' ? JSON.stringify(r.response).slice(0, 160) : r.error}</span> : (r.warnings || []).join('; ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="mt-4 text-[11px] text-text-dim">
        SNK: {config.lines.snk} · BNO: {config.lines.bno} · UAS: {config.lines.uas} · host {config.bno_host}
      </div>
    </div>
  );
}

// CSV needs the JWT, so fetch it as a blob rather than a bare link.
async function downloadCsv(runId) {
  const token = localStorage.getItem('tracon_token');
  const res = await fetch(`/api/ctr/dev/bno/export.csv?run=${encodeURIComponent(runId)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) { toast.error(`CSV export failed: HTTP ${res.status}`); return; }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `bno-sweep-${runId.slice(0, 8)}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
